import { skipToken, type QueryClient } from '@tanstack/react-query'
import { trpc } from '@/main'
import { deriveProfiles } from './use-root-search'
import { teamGetTeamFilters } from './use-team'
import type { RootSearch } from '@/routes/__root'
import type { Profile } from 'shared'

type ReportInput = Exclude<Parameters<typeof trpc.reports.getReport.queryOptions>[0], typeof skipToken>

// The ONLY search keys the server `getReport` consumes (mirror of `Profiles` in apps/shared). The
// report query key MUST be built from JUST these — never the whole search object. Spreading all of
// `search` (q, p, the *Sort params, pageType, …) into the input made `q` part of the query key, so
// the report refetched (running its loading bar) on every list-search keystroke even though its data
// doesn't depend on `q`. List/render-only params stay out of the key; the report is now stable.
const REPORT_INPUT_KEYS = [
  'first',
  'second',
  'selected',
  'type',
  'orgType',
  'teamId',
  'evaluation',
  'members',
  'name',
] as const satisfies readonly (keyof ReportInput)[]

export function reportEnabled(profiles: { first: unknown; second: unknown }) {
  if (!profiles.first && !profiles.second) return skipToken
  const input = {} as ReportInput
  for (const key of REPORT_INPUT_KEYS) {
    const value = (profiles as Record<string, unknown>)[key]
    if (value !== undefined) (input as Record<string, unknown>)[key] = value
  }
  return input
}

/**
 * SINGLE SOURCE of the `getReport` query options, referenced by the loader prefetch
 * (`prefetchReport`) AND the component (`ReportLoaded`) — so the streamed key lines up with the
 * component's `useSuspenseQuery` (route query co-location convention, see AGENTS.md). The input
 * derivation itself is already shared via `deriveProfiles` + `reportEnabled`; this wraps the one
 * `trpc.reports.getReport.queryOptions(...)` call so it lives in a single place.
 */
export function reportQueryOptions(input: ReportInput) {
  return trpc.reports.getReport.queryOptions(input)
}

export type ReportSearchDeps = Pick<RootSearch, (typeof REPORT_INPUT_KEYS)[number] | 'o' | 'od'>

/**
 * `loaderDeps` picker for every report-family route: EXACTLY the search keys the report input consumes
 * (REPORT_INPUT_KEYS), so the router's deps memoization re-runs those loaders only when the report can
 * actually change — not on unrelated search churn (dialog-open ids, `q` keystrokes). `o`/`od` ride along
 * because the profile-team prefetch below folds them into the getTeam filters (the component computes
 * its key the same way via teamGetTeamFilters).
 */
export function reportSearchDeps(search: ReportSearchDeps): ReportSearchDeps {
  return {
    first: search.first,
    second: search.second,
    selected: search.selected,
    type: search.type,
    orgType: search.orgType,
    teamId: search.teamId,
    evaluation: search.evaluation,
    members: search.members,
    name: search.name,
    o: search.o,
    od: search.od,
  }
}

export function prefetchReport(
  queryClient: QueryClient,
  search: ReportSearchDeps,
  params: Parameters<typeof deriveProfiles>[1],
  isReportPage = false,
  isMarketPage = false,
) {
  const profiles = deriveProfiles(search, params, isReportPage, 'search', isMarketPage)
  // A team/member profile in either slot makes the report sections read that team's `getTeam`
  // (EvaluationSelector / useCanSeeEvaluationSource via `useTeam(profile id)`). The team id and filters
  // derive from SEARCH + params — exactly what this loader receives — so prefetch it here with the
  // identical key (shared teamGetTeamFilters derivation). On teams routes this is the same key the
  // $teamId chrome already prefetches, so React Query dedupes it into one fetch.
  for (const profile of [profiles.first, profiles.second] as (Profile | undefined)[]) {
    const teamId = profile?.type === 'team' ? profile.id : profile?.type === 'member' ? profile.teamId : undefined
    if (teamId == null) continue
    const { filters } = teamGetTeamFilters({ ...search, ...profiles }, teamId)
    queryClient.prefetchQuery(trpc.teams.getTeam.queryOptions({ teamId, filters }))
  }
  const input = reportEnabled({ ...search, ...profiles })
  // No profile selected (e.g. /market before picking one): nothing to fetch — the component renders
  // its empty/selection state.
  if (input === skipToken) return undefined
  // Return the promise so a loader can AWAIT it on the server (complete SSR HTML, no streaming skeleton
  // on reload). prefetchQuery never rejects, so awaiting it is crash-safe.
  return queryClient.prefetchQuery(reportQueryOptions(input))
}
